import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Box,
    Button,
    ButtonGroup,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Stack,
    Textarea,
    useMediaQuery,
} from "@chakra-ui/react";
import { Content, ContentAttachment } from "../../../common/types";
import { ExternalLink } from "../../../common/components/Links";
import { ActionsMenu } from "../../Contents/components/ContentActions";
import ContentContext from "../../../common/context/ContentContext";
import { debounce } from "../../../common/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBars,
    faChalkboard,
    faStamp,
} from "@fortawesome/free-solid-svg-icons";
import TinyEditor from "../../Contents/components/TinyEditor";
import ContentAttachments from "./ContentAttachments";
import CloseButton from "./CloseButton";
import SaveButton from "./SaveButton";
import FavoriteAction from "./FavoriteButton";
import RelatedContentButton from "./RelatedContentButton";
import KnowledgeCardButton from "./KnowledgeCardButton";
import UploadFile from "./UploadFile";
import { SizeAwareComponent } from "../../../store/types";

export type DataInput = Pick<Content, "title" | "content" | "url">;
const DEBOUNCE_TIME = 60_000;

interface OwnProps {
    titleRef: React.MutableRefObject<string>;
    contentRef: React.MutableRefObject<string>;
    keyConceptsRef: React.MutableRefObject<string>;
    openQuestionsRef: React.MutableRefObject<string>;
    isAllSavedRef: React.MutableRefObject<boolean>;
    attachments: ContentAttachment[];
    onAttachmentUploaded: (a: ContentAttachment) => void;
    onLinkContent: () => void;
    patchContent: (payload?: object) => void;
    onSave: () => void;
    isSaving: boolean;
    onCloseWithAutosave?: () => void;
}

const ContentForm = ({
    titleRef,
    contentRef,
    keyConceptsRef,
    openQuestionsRef,
    isAllSavedRef,
    attachments,
    onAttachmentUploaded,
    onLinkContent,
    patchContent,
    isSaving,
    onSave,
    onCloseWithAutosave,
}: OwnProps) => {
    const content = useContext(ContentContext);
    const changesNotified = useRef(false);
    const [isAtLeastMedium] = useMediaQuery("(min-width: 48em)");
    const [isLarge] = useMediaQuery("(min-width: 1200px)");
    const [showMetaContent, setShowMetaContent] = useState(
        !!content?.keyConcepts || !!content?.openQuestions || !!content?.attachments.length
    );
    useEffect(() => {
        setShowMetaContent(isAtLeastMedium && (!!content?.keyConcepts || !!content?.openQuestions || !!content?.attachments.length))
    }, [isAtLeastMedium]);

    const notifyChanges = () => {
        if (!changesNotified.current) {
            changesNotified.current = true;
            debounce(() => {
                patchContent({
                    title: titleRef.current,
                    content: contentRef.current,
                    keyConcepts: keyConceptsRef.current,
                    openQuestions: openQuestionsRef.current,
                });
                changesNotified.current = false;
            }, DEBOUNCE_TIME);
        }
    };

    const onChangeText = (
        { target }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ref: React.MutableRefObject<string>
    ) => {
        ref.current = target.value;
        isAllSavedRef.current = false;
        notifyChanges();
    };
    const subSectionTextColor = "gray.700";

    const MetaContentAccordion = () => (
        <Accordion allowToggle defaultIndex={0}>
            <AccordionItem border={0}>
                <AccordionButton>
                    <FormLabel color={subSectionTextColor} fontSize="xl" flex={1}>
                        Conceptos clave {content?.keyConcepts && "*"}
                    </FormLabel>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                    <Textarea
                        placeholder="Escribe las ideas importantes, fechas, personajes, etc"
                        rows={16}
                        resize="vertical"
                        defaultValue={keyConceptsRef.current}
                        onChange={(e) => {
                            onChangeText(e, keyConceptsRef);
                        }}
                    />
                </AccordionPanel>
            </AccordionItem>
            <AccordionItem border={0}>
                <AccordionButton>
                    <FormLabel color={subSectionTextColor} fontSize="xl" flex={1}>
                        Dudas {content?.openQuestions && "*"}
                    </FormLabel>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                    <Textarea
                        placeholder="¿Tengo que averiguar algo más? ¿Necesito ir más allá en algo?"
                        rows={16}
                        resize="vertical"
                        defaultValue={openQuestionsRef.current}
                        onChange={(e) => {
                            onChangeText(e, openQuestionsRef);
                        }}
                    />
                </AccordionPanel>
            </AccordionItem>
            <AccordionItem border={0}>
                <AccordionButton>
                    <FormLabel color={subSectionTextColor} fontSize="xl" flex={1}>
                        Archivos {attachments.length > 0 && <span>({attachments.length})</span>}
                    </FormLabel>
                    <AccordionIcon />
                </AccordionButton>
                <AccordionPanel>
                    <ContentAttachments contentId={content!._id} attachments={attachments} />
                </AccordionPanel>
            </AccordionItem>
        </Accordion>
    );

    const ToggleMetaContentButton = ({ isMinimumSize }: SizeAwareComponent) => (
        <Button
            rightIcon={
                <FontAwesomeIcon
                    icon={showMetaContent ? faChalkboard : faBars}
                />
            }
            onClick={() => {
                setShowMetaContent(showMetaContent ? false : true);
            }}
            iconSpacing={0}
            title="Conceptos clave, Dudas y Archivos adjuntos"
            variant={isMinimumSize ? "outline" : "ghost"}
            padding={isMinimumSize ? undefined : 0}
            margin={isMinimumSize ? undefined : 0}
            minWidth={isMinimumSize ? undefined : "5"}
        />
    );

    const TitleInput = () => <Input
        variant="unstyled"
        fontSize={{ base: "2xl", md: "3xl" }}
        type="text"
        placeholder="Add a Title"
        _placeholder={{
            color: "gray",
            fontWeight: "bold",
        }}
        isRequired
        defaultValue={content!.title}
        onChange={(e) => {
            onChangeText(e, titleRef);
        }}
        color="gray.800"
    />;

    return (
        <Box marginTop={1}>
            <FormControl isRequired>
                <Stack flexDirection="row">
                    <TitleInput />
                    <ButtonGroup spacing={1} justifyContent={"end"}>
                        <SaveButton isSaving={isSaving} onSave={onSave} isMinimumSize={isAtLeastMedium} />
                        <ToggleMetaContentButton isMinimumSize={isAtLeastMedium} />
                        <ActionsMenu isMinimumSize={isAtLeastMedium} />
                        <CloseButton onCloseWithAutosave={onCloseWithAutosave} isMinimumSize={isAtLeastMedium} />
                    </ButtonGroup>
                </Stack>
            </FormControl>
            <FormControl marginY={4}>
                {content!.url && <ExternalLink url={content!.url} size="xl" />}
            </FormControl>
            <FormControl marginY={2}>
                <ButtonGroup spacing={1}>
                    <FavoriteAction isMinimumSize={isAtLeastMedium} />
                    <RelatedContentButton onLinkContent={onLinkContent} isMinimumSize={isAtLeastMedium} />
                    <KnowledgeCardButton isMinimumSize={isAtLeastMedium} />
                    <UploadFile onAttachmentUploaded={onAttachmentUploaded} isMinimumSize={isAtLeastMedium} />
                </ButtonGroup>
            </FormControl>

            <HStack h="100vh" w="100%" alignItems="flex-start">
                <Box
                    w={isAtLeastMedium && showMetaContent ? (isLarge ? "70%" : "50%") : "98%"}
                    hidden={!isAtLeastMedium && showMetaContent}
                    h="100vh"
                >
                    <TinyEditor defaultValue={content?.content ?? ""} onChange={(innerHTML: string) => {
                        contentRef.current = innerHTML;
                        notifyChanges();
                    }} />
                </Box>
                <Box
                    w={isAtLeastMedium ? (isLarge ? "30%" : "50%") : "98%"}
                    hidden={!showMetaContent}
                >
                    <MetaContentAccordion />
                </Box>
            </HStack>
        </Box>
    );
};

export default ContentForm;
